import { initializeProspect, loadProspect } from './init-flow.js';
import { renderInitPanel } from './init-panel.js';
import { renderCampaignEditor } from './campaign-editor.js';
import { openEditSlotModal, type SlotSnapshot, type SlotEdits } from './edit-slot-modal.js';
import { renderTemperaturePanel } from './temperature-panel.js';
import { renderWeatherPanel } from './weather-panel.js';
import { renderTornadoPanel } from './tornado-panel.js';
import { renderCameraPanel } from './camera-panel.js';
import { renderNewsPanel } from './news-panel.js';
import { DEFAULT_DISPLAY_MS, DEFAULT_TRANSITION_MS, VIDEO_POLL_MS } from '../config/app-config.js';
import { providers } from '../ai/registry.js';
import { assetUrl } from '../assets/asset-urls.js';
import { scenePrompt, type Scene } from '../config/prompts/scenes.js';
import { triggeredPrompt } from '../config/prompts/triggered.js';
import { ctaCopy } from '../config/cta-copy.js';
import { customizations, prospect as prospectSig, init as initSig, tornadoActive, triggeredOverrideMs, startStatus, type TempBand } from '../state/app-state.js';
import { postJson } from '../util/fetch-json.js';
import { clearCookie } from '../util/cookies.js';
import { sleep } from '../util/sleep.js';
import { SignController } from '../sign/sign-controller.js';
import { type RotationItem } from '../sign/rotation-engine.js';
import type { Slot, SlotId, SlotCustomization, Customizations } from '../types/manifest.js';

const SCENES: Scene[] = ['personal-loan', 'business-credit', 'agricultural'];
const DEFAULT_SCENE_IDS = ['default.personal-loan', 'default.business-credit', 'default.agricultural'];

const SLOT_DISPLAY_NAMES: Partial<Record<SlotId, string>> = {
  'logo': 'Logo',
  'default.personal-loan': 'Personal Loans',
  'default.business-credit': 'Business Line of Credit',
  'default.agricultural': 'Agricultural Loan',
  'triggered.tornado': 'Tornado Alert',
  'triggered.multiple_people': 'Multiple People',
  'triggered.single_female': 'Single Female',
  'triggered.single_male': 'Single Male',
  'news.us': 'US News',
  'news.local': 'Local News',
};

function rotationItemForSlot(slot: Slot, url: string): RotationItem | null {
  if (slot === 'logo') return null;
  const isDefault = slot.startsWith('default.');
  const id = isDefault ? slot.replace(/\.(cold|temperate|hot)$/, '') : slot;
  const source: 'default' | 'triggered' | 'news' = isDefault ? 'default' : slot.startsWith('news.') ? 'news' : 'triggered';
  return {
    id,
    kind: slot.startsWith('news.') ? 'video' : 'image',
    url,
    displayMs: DEFAULT_DISPLAY_MS,
    transitionMs: DEFAULT_TRANSITION_MS,
    enabled: source === 'default',
    source,
  };
}

export class Workspace {
  private items = new Map<string, RotationItem>();
  private bandCache = new Map<TempBand, Map<string, string>>();
  private companyName = '';
  private savedEnabled: Map<string, boolean> | null = null;

  constructor(public sign: SignController, public initHost: HTMLElement) {
    this.mountInitPanel();
  }

  private mountInitPanel(): void {
    // The Workspace constructor receives #controls-host (the .controls frame).
    // All panels become direct children of it so the frame wraps everything,
    // not just the init panel.
    const parent = this.initHost;

    // Full-width sections, in render order.
    this.mountAdminRow(parent);

    // Init panel renders into its own wrapper so collapsing it doesn't leave
    // a stray styled container behind.
    const initContainer = document.createElement('div');
    parent.append(initContainer);
    renderInitPanel(initContainer, {
      onLoad: (key) => loadProspect(key, this.hooks()),
      onInitialize: (key, prompt) => initializeProspect(key, prompt, this.hooks()),
    });

    const editorHost = document.createElement('div');
    parent.append(editorHost);
    renderCampaignEditor(editorHost, this.sign.engine, (id) => this.openEditModalFor(id));

    // Two-column grid for sensor and audience/news panels.
    const grid = document.createElement('div');
    grid.className = 'grid-2';
    const left = document.createElement('div');
    left.className = 'grid-2__col';
    const right = document.createElement('div');
    right.className = 'grid-2__col';
    grid.append(left, right);
    parent.append(grid);

    this.mountTemperaturePanel(left);
    this.mountWeatherPanel(left);
    this.mountTornadoPanel(left);
    this.mountCameraPanel(right);
    this.mountNewsPanel(right);
  }

  async hydrateIfPossible(prospectKey: string): Promise<boolean> {
    try {
      await loadProspect(prospectKey, this.hooks());
      return true;
    } catch {
      return false;
    }
  }

  private openEditModalFor(slotId: SlotId): void {
    const prospectKey = prospectSig.get();
    if (!prospectKey) {
      alert('Initialize a prospect first.');
      return;
    }
    const snapshot = this.buildSnapshot(slotId);
    openEditSlotModal({
      snapshot,
      onCommit: async (edits) => {
        await this.applyEdits(prospectKey, edits);
        return true;
      },
    });
  }

  private buildSnapshot(slotId: SlotId): SlotSnapshot {
    const cur = customizations.get().slots[slotId];

    if (slotId === 'logo') {
      return {
        slotId, kind: 'logo', displayName: 'Logo',
        imagePrompt: cur?.imagePrompt ?? '',
      };
    }
    if (slotId === 'default.personal-loan' || slotId === 'default.business-credit' || slotId === 'default.agricultural') {
      const scene = slotId.split('.')[1] as Scene;
      const builtinCta = ctaCopy[slotId as keyof typeof ctaCopy] ?? null;
      const builtinName = SLOT_DISPLAY_NAMES[slotId] ?? slotId;
      return {
        slotId, kind: 'default',
        displayName: cur?.name ?? builtinName,
        name: cur?.name ?? builtinName,
        message: cur?.message ?? (builtinCta ? (builtinCta as { ad: string }).ad : ''),
        cta: cur?.cta ?? (builtinCta ? builtinCta.cta : ''),
        imagePrompt: cur?.imagePrompt ?? scenePrompt(scene, 'temperate', this.companyName || 'the company'),
        imagePromptCold: cur?.imagePromptCold ?? scenePrompt(scene, 'cold', this.companyName || 'the company'),
        imagePromptHot: cur?.imagePromptHot ?? scenePrompt(scene, 'hot', this.companyName || 'the company'),
      };
    }
    if (slotId === 'triggered.tornado') {
      return {
        slotId, kind: 'tornado',
        displayName: 'Tornado Alert',
        bannerText: customizations.get().tornadoBannerText ?? '⚠ WARNING — TAKE SHELTER',
        imagePrompt: cur?.imagePrompt ?? triggeredPrompt('tornado', this.companyName || 'the company'),
      };
    }
    if (slotId === 'triggered.multiple_people' || slotId === 'triggered.single_female' || slotId === 'triggered.single_male') {
      const scene = slotId.split('.')[1] as 'multiple_people' | 'single_female' | 'single_male';
      const builtinCta = ctaCopy[slotId as keyof typeof ctaCopy] ?? null;
      const builtinName = SLOT_DISPLAY_NAMES[slotId] ?? slotId;
      return {
        slotId, kind: 'triggered_camera',
        displayName: builtinName,
        message: cur?.message ?? (builtinCta ? (builtinCta as { ad: string }).ad : ''),
        cta: cur?.cta ?? (builtinCta ? builtinCta.cta : ''),
        imagePrompt: cur?.imagePrompt ?? triggeredPrompt(scene, this.companyName || 'the company'),
      };
    }
    if (slotId === 'news.us' || slotId === 'news.local') {
      return {
        slotId, kind: 'news',
        displayName: slotId === 'news.us' ? 'US News' : 'Local News',
        geminiPrompt: cur?.geminiPrompt ?? '',
        klingPrompt: cur?.klingPrompt ?? '',
      };
    }
    // Unreachable given SlotId union but satisfies the compiler.
    return { slotId, kind: 'default', displayName: slotId };
  }

  private async applyEdits(prospect: string, edits: SlotEdits): Promise<void> {
    const { slotId, changed, next, logoUpload } = edits;
    const cur = customizations.get();
    const slot: SlotCustomization = { ...(cur.slots[slotId] ?? {}) };

    if (changed.has('name') && next.name !== undefined) slot.name = next.name;
    if (changed.has('message') && next.message !== undefined) slot.message = next.message;
    if (changed.has('cta') && next.cta !== undefined) slot.cta = next.cta;
    if (changed.has('imagePrompt') && next.imagePrompt !== undefined) slot.imagePrompt = next.imagePrompt;
    if (changed.has('imagePromptCold') && next.imagePromptCold !== undefined) slot.imagePromptCold = next.imagePromptCold;
    if (changed.has('imagePromptHot') && next.imagePromptHot !== undefined) slot.imagePromptHot = next.imagePromptHot;
    if (changed.has('geminiPrompt') && next.geminiPrompt !== undefined) slot.geminiPrompt = next.geminiPrompt;
    if (changed.has('klingPrompt') && next.klingPrompt !== undefined) slot.klingPrompt = next.klingPrompt;

    let nextRoot: Customizations = { ...cur, slots: { ...cur.slots, [slotId]: slot } };
    if (slotId === 'triggered.tornado' && changed.has('bannerText') && next.bannerText !== undefined) {
      nextRoot = { ...nextRoot, tornadoBannerText: next.bannerText };
    }

    // ---- Regeneration paths ----

    if (slotId === 'logo' && logoUpload) {
      const done = startStatus('Uploading logo');
      try {
        const { assetKey } = await providers.image.uploadLogo(prospect, logoUpload.fileBase64, logoUpload.mime);
        this.sign.setLogo(assetUrl(prospect, assetKey));
      } finally { done(); }
    } else if (slotId === 'logo' && changed.has('imagePrompt')) {
      const done = startStatus('Regenerating logo');
      try {
        const { assetKey } = await providers.image.generate({
          prospect, slot: 'logo', prompt: next.imagePrompt!,
          styleType: 'DESIGN', aspectRatio: '1x1', transparent: true,
          companyName: this.companyName, companyPrompt: this.companyName,
        });
        this.sign.setLogo(assetUrl(prospect, assetKey));
      } finally { done(); }
    } else if ((slotId === 'default.personal-loan' || slotId === 'default.business-credit' || slotId === 'default.agricultural') && changed.has('imagePrompt')) {
      const scene = slotId.split('.')[1]!;
      const done = startStatus(`Regenerating ${scene}`);
      try {
        const templateSlot = `default.${scene}.temperate` as Slot;
        const { assetKey } = await providers.image.generate({
          prospect, slot: templateSlot, prompt: next.imagePrompt!,
          companyName: this.companyName, companyPrompt: this.companyName,
        });
        const item = this.items.get(slotId);
        if (item) {
          item.url = assetUrl(prospect, assetKey);
          this.sign.engine.appendOrReplace(item);
        }
      } finally { done(); }
    } else if ((slotId === 'default.personal-loan' || slotId === 'default.business-credit' || slotId === 'default.agricultural') && (changed.has('imagePromptCold') || changed.has('imagePromptHot'))) {
      // Invalidate band cache so the next band crossing regenerates with the new prompt.
      if (changed.has('imagePromptCold')) this.bandCache.delete('cold');
      if (changed.has('imagePromptHot')) this.bandCache.delete('hot');
    } else if ((slotId === 'triggered.multiple_people' || slotId === 'triggered.single_female' || slotId === 'triggered.single_male') && changed.has('imagePrompt')) {
      const done = startStatus(`Regenerating ${slotId}`);
      try {
        const { assetKey } = await providers.image.generate({
          prospect, slot: slotId as Slot, prompt: next.imagePrompt!,
          companyName: this.companyName, companyPrompt: this.companyName,
        });
        const item = this.items.get(slotId);
        if (item) {
          item.url = assetUrl(prospect, assetKey);
          this.sign.engine.appendOrReplace(item);
        }
      } finally { done(); }
    } else if (slotId === 'triggered.tornado' && changed.has('imagePrompt')) {
      const done = startStatus('Regenerating tornado scene');
      try {
        const { assetKey } = await providers.image.generate({
          prospect, slot: 'triggered.tornado', prompt: next.imagePrompt!,
          companyName: this.companyName, companyPrompt: this.companyName,
        });
        const item = this.items.get('triggered.tornado');
        if (item) {
          item.url = assetUrl(prospect, assetKey);
          this.sign.engine.appendOrReplace(item);
          if (tornadoActive.get()) {
            this.sign.engine.overrideWith(item);
          }
        }
      } finally { done(); }
    } else if ((slotId === 'news.us' || slotId === 'news.local') && (changed.has('geminiPrompt') || changed.has('klingPrompt'))) {
      let klingPromptToUse: string;
      if (changed.has('geminiPrompt')) {
        const done = startStatus('Running Gemini to craft Kling prompt');
        try {
          klingPromptToUse = (await providers.text.runGemini(next.geminiPrompt!)).trim();
          if (!klingPromptToUse) throw new Error('Gemini returned an empty result');
          slot.klingPrompt = klingPromptToUse;
          nextRoot = { ...nextRoot, slots: { ...nextRoot.slots, [slotId]: slot } };
        } finally { done(); }
      } else {
        klingPromptToUse = next.klingPrompt!.trim();
      }
      const newsLabel = slotId === 'news.us' ? 'US' : 'Local';
      const done = startStatus(`Generating ${newsLabel} news clip`);
      try {
        const { requestId } = await providers.video.start({
          prospect, slot: slotId as 'news.us' | 'news.local', prompt: klingPromptToUse, durationSec: 5,
        });
        let assetKey: string | undefined;
        while (true) {
          const r = await providers.video.poll(requestId);
          if (r.status === 'done' && r.assetKey) { assetKey = r.assetKey; break; }
          if (r.status === 'error') throw new Error(r.errorMessage ?? 'video generation failed');
          await sleep(VIDEO_POLL_MS);
        }
        const newItem: RotationItem = {
          id: slotId,
          kind: 'video',
          url: assetUrl(prospect, assetKey!),
          displayMs: 20_000,
          transitionMs: DEFAULT_TRANSITION_MS,
          enabled: true,
          source: 'news',
        };
        this.items.set(slotId, newItem);
        this.sign.engine.appendOrReplace(newItem);
      } finally { done(); }
    }

    // ---- Persist customizations + refresh chrome ----
    customizations.set(nextRoot);
    await providers.text.saveCustomizations(prospect, nextRoot);
    if (slotId === 'triggered.tornado' && (changed.has('bannerText') || changed.has('imagePrompt'))) {
      this.sign.refreshChrome();
    }
  }

  private hooks() {
    return {
      onAssetReady: (slot: Slot, url: string) => {
        if (slot === 'logo') { this.sign.setLogo(url); return; }
        const item = rotationItemForSlot(slot, url);
        if (!item) return;
        this.items.set(item.id, item);
        if (slot.startsWith('default.')) {
          // Use swapDefaultsTo so triggered/news items already in the engine are
          // preserved. setItems would replace the entire engine.items array and
          // wipe any triggered scene that happened to arrive between default
          // scenes — a real bug under both init's parallel fan-out and load's
          // manifest-order iteration.
          const list = DEFAULT_SCENE_IDS
            .map((id) => this.items.get(id))
            .filter(Boolean) as RotationItem[];
          this.sign.engine.swapDefaultsTo(list);
        } else {
          this.sign.engine.appendOrReplace(item);
        }
      },
      onCompanyName: (name: string) => { this.companyName = name; },
    };
  }

  private async swapBand(band: TempBand): Promise<void> {
    const prospectKey = prospectSig.get();
    if (!prospectKey) return;
    let cache = this.bandCache.get(band);
    if (!cache) {
      cache = new Map<string, string>();
      const done = startStatus(`Generating ${band} weather scenes`);
      try {
        await Promise.all(SCENES.map(async (s) => {
          const slot: Slot = `default.${s}.${band}`;
          const slotId = `default.${s}` as const;
          const custom = customizations.get().slots[slotId];
          const customPrompt = band === 'cold' ? custom?.imagePromptCold
            : band === 'hot' ? custom?.imagePromptHot
            : custom?.imagePrompt;
          const promptText = customPrompt ?? scenePrompt(s, band, this.companyName || 'the company');
          // companyPrompt is not separately tracked in Workspace; companyName is the human-readable
          // form and is sufficient for re-persisting identity when a band-swap regeneration occurs.
          const { assetKey } = await providers.image.generate({
            prospect: prospectKey, slot, prompt: promptText,
            companyName: this.companyName, companyPrompt: this.companyName,
          });
          cache!.set(slot, assetUrl(prospectKey, assetKey));
        }));
        this.bandCache.set(band, cache);
      } finally {
        done();
      }
    }
    const replacements = SCENES.map((s) => {
      const slot: Slot = `default.${s}.${band}`;
      const url = cache!.get(slot)!;
      return {
        id: `default.${s}`,
        kind: 'image' as const,
        url,
        displayMs: 10_000,
        transitionMs: 500,
        enabled: true,
        source: 'default' as const,
      };
    });
    this.sign.engine.swapDefaultsTo(replacements);
  }

  private mountAdminRow(parent: HTMLElement): void {
    const host = document.createElement('div');
    host.className = 'panel';
    host.innerHTML = `
    <h2>Session</h2>
    <div class="row">
      <span id="adm-current" class="prospect-chip"></span>
      <button id="adm-switch" class="button">Switch Prospect</button>
      <button id="adm-reset" class="button is-danger">Reset Demo</button>
    </div>`;
    parent.append(host);
    const cur = host.querySelector<HTMLSpanElement>('#adm-current')!;
    prospectSig.subscribe((k) => {
      if (k) {
        cur.textContent = `Active prospect: ${k}`;
        cur.classList.add('prospect-chip--loaded');
        cur.classList.remove('prospect-chip--empty');
      } else {
        cur.textContent = 'No prospect loaded';
        cur.classList.add('prospect-chip--empty');
        cur.classList.remove('prospect-chip--loaded');
      }
    });
    host.querySelector<HTMLButtonElement>('#adm-switch')!.addEventListener('click', () => {
      clearCookie('sgt_prospect');
      prospectSig.set(null);
      initSig.set({ phase: 'awaiting' });
      location.reload();
    });
    host.querySelector<HTMLButtonElement>('#adm-reset')!.addEventListener('click', async () => {
      const k = prospectSig.get();
      if (!k) return;
      if (!confirm(`Reset demo for "${k}"? This wipes its assets.`)) return;
      const done = startStatus('Resetting prospect');
      try {
        await postJson<{ ok: true }>('/api/reset', { key: k });
      } finally {
        done();
      }
      clearCookie('sgt_prospect');
      prospectSig.set(null);
      location.reload();
    });
  }

  private mountTemperaturePanel(parent: HTMLElement): void {
    const host = document.createElement('div');
    parent.append(host);
    renderTemperaturePanel(host, { onBandCross: (b) => this.swapBand(b) });
  }

  private mountWeatherPanel(parent: HTMLElement): void {
    const host = document.createElement('div');
    parent.append(host);
    renderWeatherPanel(host);
  }

  private mountTornadoPanel(parent: HTMLElement): void {
    const host = document.createElement('div');
    parent.append(host);
    renderTornadoPanel(host, {
      onToggle: (active) => {
        if (active) {
          // Capture current enabled state, then disable all campaign items.
          this.savedEnabled = new Map();
          for (const [id, item] of this.items.entries()) {
            this.savedEnabled.set(id, item.enabled);
            if (item.enabled) {
              item.enabled = false;
              this.sign.engine.disable(id);
            }
          }
          const tornado = this.items.get('triggered.tornado');
          if (tornado) this.sign.engine.overrideWith(tornado);
        } else {
          this.sign.engine.clearOverride();
          if (this.savedEnabled) {
            for (const [id, wasEnabled] of this.savedEnabled.entries()) {
              const it = this.items.get(id);
              if (it) it.enabled = wasEnabled;
              if (wasEnabled) this.sign.engine.enable(id);
              else this.sign.engine.disable(id);
            }
            this.savedEnabled = null;
          }
        }
      },
    });
  }

  private mountCameraPanel(parent: HTMLElement): void {
    const host = document.createElement('div');
    parent.append(host);
    const CAMERA_SLOTS = ['triggered.single_female', 'triggered.single_male', 'triggered.multiple_people'] as const;
    renderCameraPanel(host, {
      onClassified: (label) => {
        // Tornado is a higher-priority signal; leave camera state alone while it's active.
        if (tornadoActive.get()) return;

        const targetSlot =
          label === 'SINGLE_FEMALE' ? 'triggered.single_female' :
          label === 'SINGLE_MALE' ? 'triggered.single_male' :
          label === 'MULTIPLE_PEOPLE' ? 'triggered.multiple_people' : null;

        const displayMs = triggeredOverrideMs.get();

        // For each camera-triggered slot:
        //   - if it matches the new classification, enable it and update its display time;
        //   - otherwise disable it (only if currently enabled, to avoid an extra kick).
        for (const slot of CAMERA_SLOTS) {
          const item = this.items.get(slot);
          if (!item) {
            if (slot === targetSlot) {
              console.warn(
                `[sgt-signage] Camera classified ${label} but slot "${slot}" is not loaded. ` +
                `Click "Reset Demo" and reinitialize to regenerate this asset.`,
              );
            }
            continue;
          }
          if (slot === targetSlot) {
            // Mutate the workspace-side ref too so the tornado handler's
            // savedEnabled snapshot picks up the right state later.
            item.enabled = true;
            item.displayMs = displayMs;
            this.sign.engine.setDisplayMs(slot, displayMs);
            if (!this.sign.engine.isEnabled(slot)) this.sign.engine.enable(slot);
          } else if (this.sign.engine.isEnabled(slot)) {
            item.enabled = false;
            this.sign.engine.disable(slot);
          }
        }

        // If we have a target, snap the rotation straight to it so the operator
        // sees the camera-triggered scene immediately. After displayMs the engine
        // moves on to the next item in the rotation as usual, eventually cycling
        // back to the triggered scene until classification changes.
        if (targetSlot) this.sign.engine.showNow(targetSlot);
      },
    });
  }

  private mountNewsPanel(parent: HTMLElement): void {
    const host = document.createElement('div');
    parent.append(host);
    renderNewsPanel(host, () => prospectSig.get(), {
      onNewsReady: (slot, url, story) => {
        const newItem = {
          id: slot,
          kind: 'video' as const,
          url,
          displayMs: 20_000,
          transitionMs: DEFAULT_TRANSITION_MS,
          enabled: true,
          source: 'news' as const,
          caption: story.summary,
        };
        this.items.set(slot, newItem);
        this.sign.engine.appendOrReplace(newItem);
      },
    });
  }
}
